import { useTranslations } from 'next-intl';
import { ContentTypeEnum } from '@/api/thread';
import Toast from '@/components/Toast';
import {
  UploadType,
  ActionTypeEnum,
  IEditorContentItem,
  IThreadValue,
} from '@/components/ModuleEditor/config';
import { MAX_VIDEOS, MAX_PHOTOS } from '@/constants/upload';
import { MAX_DESC_LENGTH } from '@/constants';
import { checkArticalContentByTrim } from '@/utils/thread';

export enum FileUploadStatusEnum {
  pending = 'pending',
  error = 'error',
  success = 'success',
}

/**
 * 计算帖子内的总字数
 * @param message 模块化数据列表
 * @returns number
 */
export const calcTextLength = (message: IEditorContentItem[] = []) => {
  let i = 0;
  message.forEach(item => {
    if(item.type === ContentTypeEnum.text) {
      i += item?.desc?.length||0;
    }
  });
  return i;
};

export type IValidFileMaxCountParams = {
  /** 上传类型 */
  type?: UploadType;
  /** 文件最大数量阈值 */
  fileMaxCount?: number;
  /** 提示文案key */
  langKey: string;
  /** 校验数据源 */
  data?: IEditorContentItem[];
};

export type IValidFileUploadStatusParams = {
  /** 校验数据源 */
  data?: IEditorContentItem[];
};

export interface IUseValidEditArticalProps {
  canPost?: boolean;
}

const useValidEditArtical = ({ canPost }: IUseValidEditArticalProps = {}) => {
  const t = useTranslations();

  /**
   * 校验用户名是否有发帖权限
   * @param
   * @returns boolean
   */
  const validUserEditPermission = () => {
    if (!canPost) {
      Toast.show({
        content: t('tips_post_credit_error'),
      });
      return false;
    }
    return true;
  };

  /**
   * 校验标题和正文是否为空
   * @param content
   * @returns boolean
   */
  const validEmptyContent = (content: IThreadValue, showToast: boolean = true) => {
    const toastTextKey = checkArticalContentByTrim(content || undefined);
    if (toastTextKey) {
      if (showToast) {
        Toast.show({
          content: t(toastTextKey),
        });
      }
      return false;
    }
    return true;
  };

  /**
   * 校验正文文本长度是否<=200000
   * @param content
   * @returns boolean
   */
  const validTextContentLength = (content: IThreadValue['message'] = []) => {
    return calcTextLength(content) <= MAX_DESC_LENGTH;
  };

  /**
   * 校验上传文件最大数量：最多上传3个视频 10张图片
   * @param
   * @returns boolean
   */
  const validFileMaxCount = ({
    type,
    fileMaxCount,
    langKey,
    data = [],
  }: IValidFileMaxCountParams) => {
    const filesLength =
      type === ActionTypeEnum.video
        ? data.filter((item) => item.type === ContentTypeEnum.video).length
        : data.filter((item) => item.type === ContentTypeEnum.photo).length;
    const MAX_COUNT = type === ActionTypeEnum.video ? MAX_VIDEOS : MAX_PHOTOS;
    const max = fileMaxCount || MAX_COUNT;
    if (filesLength >= max) {
      Toast.show({
        content: t(langKey, { count: MAX_COUNT }),
      });
      return false;
    }
    return true;
  };

  /**
   * 校验文件上传状态
   * @param param
   * @returns boolean
   */
  const validFileUploadStatus = ({
    data = [],
  }: IValidFileUploadStatusParams): FileUploadStatusEnum => {
    const { photos, videos } = data.reduce(
      (prev, cur) => {
        if (cur.type === ContentTypeEnum.photo) {
          prev = { ...prev, photos: [...prev.photos, cur] };
        }
        if (cur.type === ContentTypeEnum.video) {
          prev = { ...prev, videos: [...prev.videos, cur] };
        }
        return prev;
      },
      { photos: [], videos: [] } as { [key: string]: IEditorContentItem[] }
    );

    /** 校验是否存在上传中文件 */
    const hasUploading = [...photos, ...videos].filter(
      (item: IEditorContentItem) =>
        !item?.error && (item.percent !== 1 || !item.isCompleted)
    );
    if (hasUploading.length > 0) {
      Toast.show({
        content: t('error_post_unfinished_upload'),
      });
      return FileUploadStatusEnum.pending;
    }

    /** 校验是否存在上传异常文件 */
    const hasUploadFileError = [...photos, ...videos].filter(
      (item) => item?.error
    );
    if (hasUploadFileError.length > 0) {
      return FileUploadStatusEnum.error;
    }
    return FileUploadStatusEnum.success;
  };

  return {
    validUserEditPermission,
    validEmptyContent,
    validTextContentLength,
    validFileMaxCount,
    validFileUploadStatus,
  };
};

export default useValidEditArtical;
